import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import BadgeNotification from './BadgeNotification';
import './FeedbackModal.css';

function FeedbackModal({ show, onClose }) {
  const navigate = useNavigate();
  const [easeRating, setEaseRating] = useState(0);
  const [qualityRating, setQualityRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [badgeEarned, setBadgeEarned] = useState(null);

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Screenshot must be less than 5MB');
        return;
      }

      setScreenshot(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      // Use FormData if screenshot is included, otherwise JSON
      let response;

      if (screenshot) {
        const formData = new FormData();
        formData.append('url', window.location.pathname);
        formData.append('ease_rating', easeRating || '');
        formData.append('quality_rating', qualityRating || '');
        formData.append('feedback_text', feedbackText);
        formData.append('screenshot', screenshot);

        response = await fetch('/api/feedback/submit', {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
      } else {
        response = await fetch('/api/feedback/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            url: window.location.pathname,
            ease_rating: easeRating || null,
            quality_rating: qualityRating || null,
            feedback_text: feedbackText
          })
        });
      }

      if (response.ok) {
        const data = await response.json();
        setSubmitted(true);
        if (data.badge_earned) {
          setBadgeEarned(data.badge_earned);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to submit feedback');
      }
    } catch (err) {
      setError('Error submitting feedback: ' + err.message);
    }

    setSubmitting(false);
  };

  const handleClose = () => {
    // Reset form
    setEaseRating(0);
    setQualityRating(0);
    setFeedbackText('');
    setScreenshot(null);
    setScreenshotPreview(null);
    setSubmitted(false);
    setBadgeEarned(null);
    setError('');
    onClose();
  };

  const StarRating = ({ rating, setRating, label }) => {
    return (
      <div className="star-rating-group">
        <Form.Label>{label}</Form.Label>
        <div className="star-rating">
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              className={`star ${rating >= star ? 'filled' : ''}`}
              onClick={() => setRating(star)}
              style={{ cursor: 'pointer' }}
            >
              ★
            </span>
          ))}
        </div>
      </div>
    );
  };

  if (submitted) {
    return (
      <>
        <Modal show={show} onHide={handleClose} centered>
          <Modal.Header closeButton>
            <Modal.Title>Thank You! 🎉</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center py-3">
              <h5>Your feedback has been submitted!</h5>
              <p className="text-muted mt-3">
                Your insights help us make AI Profit Engineer better for everyone.
                We review every submission and prioritize the most impactful changes.
              </p>
              {badgeEarned && (
                <div className="mt-4">
                  <div className="badge-display">
                    <h4>🏆 Badge Unlocked!</h4>
                    <div className="badge-name">{badgeEarned.name}</div>
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {badgeEarned && (
          <BadgeNotification badge={badgeEarned} />
        )}
      </>
    );
  }

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>💬 Share Your Feedback</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <p className="text-muted small mb-4">
            Help us improve your experience! Your feedback is anonymous and helps shape future updates.
          </p>

          <StarRating
            rating={easeRating}
            setRating={setEaseRating}
            label="How easy was this to use?"
          />

          <StarRating
            rating={qualityRating}
            setRating={setQualityRating}
            label="How useful was the output?"
          />

          <Form.Group className="mt-3">
            <Form.Label>Any suggestions or issues? (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Tell us what you think..."
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Screenshot (Optional)</Form.Label>
            <p className="text-muted small mb-2">
              Upload a screenshot to help us see what you're seeing
            </p>
            {!screenshotPreview ? (
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleScreenshotChange}
              />
            ) : (
              <div>
                <div className="screenshot-preview mb-2">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}
                  />
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRemoveScreenshot}
                >
                  Remove Screenshot
                </Button>
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="link"
            className="text-muted p-0"
            onClick={() => { handleClose(); navigate('/roadmap'); }}
          >
            🗺️ View Product Roadmap
          </Button>
          <div>
            <Button variant="outline-secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={submitting || (!easeRating && !qualityRating && !feedbackText)}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default FeedbackModal;
